// Top-level panel board editor component.
// Page passes BoardLayout; all layout normalization, edit state, and rendering
// are hidden inside this module. PanelBoardPage does not know about EditablePanelLayout.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import type { BoardLayout } from '@/types/api';
import { Button } from '@/components/ui/Button';
import { formatRcdGroupLabel } from '@/lib/electricalLabels';
import {
  buildEditableLayout,
  canPlaceDevice,
  canSwapDevices,
  createRcdGroupForCircuit,
  deleteRcdGroup,
  editableToDisplayLayout,
  getCircuitGroupMoveBlockReason,
  getCircuitForDevice,
  getDeviceAt,
  getRcdGroupDeleteBlockReason,
  moveDevice,
  moveCircuitToGroup,
  reflowEditableLayout,
  setCircuitLabel,
  setDeviceRating,
  swapDevices,
} from './editLayout';
import { PanelBoardCanvas } from './PanelBoardCanvas';
import { GroupsLinesPanel } from './GroupsLinesPanel';
import { buildGroupingModel, type FocusTarget } from './grouping';
import {
  ENCLOSURE_VARIANTS,
  buildEnclosureGeometry,
  enclosureById,
} from './enclosures/registry';
import type { EditablePanelLayout } from './types';

interface PanelBoardEditorProps {
  layout: BoardLayout;
}

function shortCircuitLabel(circuit: { rebt_code?: string | null; circuit_id: string; label: string }): string {
  const fromCode = circuit.rebt_code?.match(/C\d+[A-Z]?/i)?.[0];
  if (fromCode) return fromCode.toUpperCase();
  const fromId = circuit.circuit_id.match(/C\d+[A-Z]?/i)?.[0];
  if (fromId) return fromId.toUpperCase();
  const fromLabel = circuit.label.match(/C\d+[A-Z]?/i)?.[0];
  if (fromLabel) return fromLabel.toUpperCase();
  return circuit.label.slice(0, 5).toUpperCase();
}

// Standard single-pole MCB ratings offered when editing a breaker. 10/16/20/25/32
// have SVG symbols; others render the labeled rectangle fallback.
const MCB_RATINGS = [6, 10, 16, 20, 25, 32, 40] as const;

// Smallest enclosure (variants ordered by capacity) whose rails hold the reflowed
// layout at that enclosure's rail width; falls back to the largest. Must reflow with
// the same compact flag the editor uses, otherwise the preview gaps over-size the pick.
function defaultEnclosureId(layout: BoardLayout, compact: boolean): string {
  for (const v of ENCLOSURE_VARIANTS) {
    if (buildEditableLayout(layout, v.modulesPerRow, { compact }).totalRows <= v.rows) return v.id;
  }
  return ENCLOSURE_VARIANTS[ENCLOSURE_VARIANTS.length - 1].id;
}

function fittingEnclosureIdForEditable(editable: EditablePanelLayout, compact: boolean): string {
  for (const v of ENCLOSURE_VARIANTS) {
    if (reflowEditableLayout(editable, v.modulesPerRow, { compact }).totalRows <= v.rows) return v.id;
  }
  return ENCLOSURE_VARIANTS[ENCLOSURE_VARIANTS.length - 1].id;
}

// ─── Editor ───────────────────────────────────────────────────────────────────

export function PanelBoardEditor({ layout }: PanelBoardEditorProps) {
  const { t } = useTranslation();

  // When true, inter-group preview gaps are dropped so devices sit flush and the
  // enclosure auto-fit reflects the real module count ("remove empty modules").
  const [compact, setCompact] = useState(false);
  const [enclosureId, setEnclosureId] = useState(() => defaultEnclosureId(layout, false));
  const [editable, setEditable] = useState(() =>
    buildEditableLayout(layout, enclosureById(enclosureId).modulesPerRow, { compact: false }),
  );
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(null);
  // Drives the group/line ↔ canvas highlight; independent of move-selection.
  const [focus, setFocus] = useState<FocusTarget | null>(null);
  // Screen point of the open device action menu (popover), or null when closed.
  const [menuAnchor, setMenuAnchor] = useState<{ x: number; y: number } | null>(null);
  const [groupLimitBanner, setGroupLimitBanner] = useState<{ circuitId: string } | null>(null);
  const [isModified, setIsModified] = useState(false);
  const [printDisclaimerOpen, setPrintDisclaimerOpen] = useState(false);

  // Re-initialize local edit state when the backend layout changes (different design opened).
  // Intentional dep on layout_id only — full layout object reference changes on every refetch.
  useEffect(() => {
    const id = defaultEnclosureId(layout, false);
    setEnclosureId(id);
    setEditable(buildEditableLayout(layout, enclosureById(id).modulesPerRow, { compact: false }));
    setSelectedDeviceId(null);
    setFocus(null);
    setGroupLimitBanner(null);
    setCompact(false);
    setIsModified(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layout.layout_id]);

  const geometry = useMemo(() => buildEnclosureGeometry(enclosureById(enclosureId)), [enclosureId]);
  const displayLayout = useMemo(() => editableToDisplayLayout(editable), [editable]);

  // Group/line relationships are derived from the editor's current editable state,
  // so local line renames and group reassignment show in the panel + highlight.
  const grouping = useMemo(
    () => buildGroupingModel(editable.circuits, editable.groups),
    [editable.circuits, editable.groups],
  );
  const highlight = useMemo(() => grouping.resolveHighlight(focus), [grouping, focus]);
  const deviceLineLabels = useMemo(() => {
    const labels = new Map<string, string>();
    for (const circuit of editable.circuits) {
      if (circuit.protection_device_id) {
        labels.set(circuit.protection_device_id, shortCircuitLabel(circuit));
      }
    }
    // Label each RCD device with its group abbreviation (ID1, ID2, ...), using
    // the same numbering as the groups/lines panel so the two stay in sync.
    grouping.groups.forEach((group, index) => {
      if (group.protection_device_id) {
        labels.set(group.protection_device_id, formatRcdGroupLabel(t, index));
      }
    });
    return labels;
  }, [editable.circuits, grouping.groups, t]);

  // True when placed devices need more rails than the chosen enclosure provides.
  // Do not use editable.totalRows here: it can include trailing reserve slots or
  // stale empty rows after local edits. The warning must reflect actual devices.
  const requiredDeviceRows = useMemo(() => {
    let maxRow = 0;
    for (const [deviceId, placement] of editable.placements) {
      if (editable.devices.has(deviceId)) maxRow = Math.max(maxRow, placement.row + 1);
    }
    return Math.max(1, maxRow);
  }, [editable.devices, editable.placements]);
  const overflowsEnclosure = requiredDeviceRows > geometry.rows;

  // Breaker editing (rating + line name) targets a single selected MCB.
  const selectedDevice = selectedDeviceId ? editable.devices.get(selectedDeviceId) ?? null : null;
  const selectedMcb = selectedDevice?.deviceType === 'mcb' ? selectedDevice : null;
  const selectedCircuit = selectedMcb ? getCircuitForDevice(editable, selectedMcb.deviceId) : null;

  // Selecting a device on the canvas both arms it for moving and focuses it for
  // the relationship highlight; deselecting clears the highlight too.
  const handleSelectDevice = useCallback((deviceId: string | null) => {
    setSelectedDeviceId(deviceId);
    setFocus(deviceId ? { kind: 'device', id: deviceId } : null);
    // The menu opens on a settled click (onOpenDeviceMenu), not on selection itself,
    // so a drag never pops it; clear any open menu here.
    setMenuAnchor(null);
  }, []);

  // Open the device action menu at the click point (device already selected).
  const handleOpenDeviceMenu = useCallback((_deviceId: string, at: { x: number; y: number }) => {
    setMenuAnchor(at);
  }, []);

  // Focusing a card from the panel highlights only; it must not arm a device for
  // moving, so any pending move-selection is cleared. Clicking the same card toggles.
  const handleFocusGroup = useCallback((groupId: string) => {
    setSelectedDeviceId(null);
    setFocus((prev) =>
      prev?.kind === 'group' && prev.id === groupId ? null : { kind: 'group', id: groupId },
    );
  }, []);

  const handleFocusCircuit = useCallback((circuitId: string) => {
    setSelectedDeviceId(null);
    setFocus((prev) =>
      prev?.kind === 'circuit' && prev.id === circuitId ? null : { kind: 'circuit', id: circuitId },
    );
  }, []);

  const handleGetCircuitGroupMoveBlockReason = useCallback(
    (circuitId: string, targetGroupId: string) =>
      getCircuitGroupMoveBlockReason(editable, circuitId, targetGroupId),
    [editable],
  );

  const handleMoveCircuitToGroup = useCallback(
    (circuitId: string, targetGroupId: string) => {
      const reason = getCircuitGroupMoveBlockReason(editable, circuitId, targetGroupId);
      if (reason === 'target_group_full') {
        setGroupLimitBanner({ circuitId });
        return;
      }
      const next = moveCircuitToGroup(editable, circuitId, targetGroupId);
      if (next !== editable) {
        setEditable(next);
        setSelectedDeviceId(null);
        setFocus({ kind: 'circuit', id: circuitId });
        setGroupLimitBanner(null);
        setIsModified(true);
      }
    },
    [editable],
  );

  const handleCreateGroupForLimitedCircuit = useCallback(() => {
    if (!groupLimitBanner) return;
    const next = createRcdGroupForCircuit(editable, groupLimitBanner.circuitId);
    if (next !== editable) {
      setEditable(next);
      setSelectedDeviceId(null);
      setFocus({ kind: 'circuit', id: groupLimitBanner.circuitId });
      setIsModified(true);
    }
    setGroupLimitBanner(null);
  }, [editable, groupLimitBanner]);

  const handleGetRcdGroupDeleteBlockReason = useCallback(
    (groupId: string) =>
      getRcdGroupDeleteBlockReason(editable, groupId),
    [editable],
  );

  const handleDeleteRcdGroup = useCallback(
    (groupId: string) => {
      const next = deleteRcdGroup(editable, groupId);
      if (next !== editable) {
        setEditable(next);
        setSelectedDeviceId(null);
        setFocus(null);
        setGroupLimitBanner(null);
        setIsModified(true);
      }
    },
    [editable],
  );

  const handleSlotClick = useCallback(
    (row: number, col: number) => {
      if (!selectedDeviceId) return;
      const next = moveDevice(editable, selectedDeviceId, row, col);
      if (next !== editable) {
        setEditable(next);
        setSelectedDeviceId(null);
        setFocus(null);
        setIsModified(true);
      }
    },
    [editable, selectedDeviceId],
  );

  const handleCanPlaceAt = useCallback(
    (row: number, col: number) => {
      if (!selectedDeviceId) return false;
      return canPlaceDevice(editable, selectedDeviceId, row, col);
    },
    [editable, selectedDeviceId],
  );

  // True when dropping the dragged device on (row, col) would swap it with the
  // device already there (used by the canvas to flag swap targets during a drag).
  const handleCanSwapAt = useCallback(
    (row: number, col: number) => {
      if (!selectedDeviceId) return false;
      const occupant = getDeviceAt(editable, row, col);
      if (occupant === null || occupant === selectedDeviceId) return false;
      return canSwapDevices(editable, selectedDeviceId, occupant);
    },
    [editable, selectedDeviceId],
  );

  // Drag-and-drop commit: called by canvas with deviceId known directly,
  // avoiding the stale-closure problem of handleSlotClick. Dropping on a free slot
  // moves; dropping on another device swaps their positions (so a full panel can
  // still be rearranged).
  const handleMoveDevice = useCallback(
    (deviceId: string, row: number, col: number) => {
      const occupant = getDeviceAt(editable, row, col);
      const next =
        occupant === null
          ? moveDevice(editable, deviceId, row, col)
          : occupant === deviceId
            ? editable
            : swapDevices(editable, deviceId, occupant);
      if (next !== editable) {
        setEditable(next);
        setSelectedDeviceId(null);
        setFocus(null);
        setIsModified(true);
      }
    },
    [editable],
  );

  // Change the selected breaker's ampere rating; selection is kept so the user can
  // keep adjusting. Width is unchanged; the MCB symbol follows the new rating.
  const handleChangeRating = useCallback(
    (ratingA: number) => {
      if (!selectedDeviceId) return;
      const next = setDeviceRating(editable, selectedDeviceId, ratingA);
      if (next !== editable) {
        setEditable(next);
        setIsModified(true);
      }
    },
    [editable, selectedDeviceId],
  );

  // Rename the selected breaker's line (e.g. "C5a").
  const handleChangeLineName = useCallback(
    (label: string) => {
      if (!selectedDeviceId) return;
      const circuit = getCircuitForDevice(editable, selectedDeviceId);
      if (!circuit) return;
      const next = setCircuitLabel(editable, circuit.circuit_id, label);
      if (next !== editable) {
        setEditable(next);
        setIsModified(true);
      }
    },
    [editable, selectedDeviceId],
  );

  const handleConfirmPrint = useCallback(() => {
    setPrintDisclaimerOpen(false);
    // Let the dialog unmount before opening the browser print dialog.
    window.setTimeout(() => window.print(), 0);
  }, []);

  const handleReset = useCallback(() => {
    const id = defaultEnclosureId(layout, compact);
    setEnclosureId(id);
    setEditable(buildEditableLayout(layout, enclosureById(id).modulesPerRow, { compact }));
    setSelectedDeviceId(null);
    setFocus(null);
    setGroupLimitBanner(null);
    setIsModified(false);
  }, [layout, compact]);

  // Remove preview gaps by reflowing the current local edit state. This is intentionally
  // one-way: Reset layout is the explicit path for returning to backend defaults/gaps.
  const handleCompact = useCallback(() => {
    const next = true;
    const id = fittingEnclosureIdForEditable(editable, next);
    const width = enclosureById(id).modulesPerRow;
    setCompact(next);
    setEnclosureId(id);
    setEditable(reflowEditableLayout(editable, width, { compact: next }));
    setSelectedDeviceId(null);
    setFocus(null);
    setGroupLimitBanner(null);
    setIsModified(true);
  }, [editable]);

  // Switching enclosures only reflows placements when the rail width changes (e.g. 12 → 16);
  // same-width switches just swap the frame. Local groups/lines/ratings are preserved.
  const handleSelectEnclosure = useCallback(
    (id: string) => {
      setEnclosureId(id);
      const nextWidth = enclosureById(id).modulesPerRow;
      if (nextWidth !== editable.modulesPerRow) {
        setEditable(reflowEditableLayout(editable, nextWidth, { compact }));
        setSelectedDeviceId(null);
        setFocus(null);
        setGroupLimitBanner(null);
      }
    },
    [editable, compact],
  );

  return (
    <div data-print-area data-panel-board className="flex flex-col gap-4">
      {/* Toolbar — hidden during print */}
      <div className="no-print flex flex-col gap-2 pb-4 border-b border-border">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="font-mono text-xs text-ink-4">
            {layout.rows}&thinsp;×&thinsp;{layout.modules_per_row}
            {' · '}
            {layout.total_modules_used}/{layout.rows * layout.modules_per_row}
            &thinsp;{t('panel_board.stat_modules')}
            {' · '}
            {layout.fits_capacity
              ? <span className="text-success">✓ {t('panel_board.stat_fits')}</span>
              : <span className="text-warning">⚠ {t('panel_board.stat_exceeds')}</span>
            }
          </div>
          <div className="flex items-center gap-2">
            {isModified && (
              <span className="font-mono text-xs text-warning">
                ⚠ {t('panel_board.modified_locally')}
              </span>
            )}
            {isModified && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleReset}
              >
                {t('panel_board.reset_layout')}
              </Button>
            )}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setPrintDisclaimerOpen(true)}
            >
              {t('panel_board.print')}
            </Button>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {/* Enclosure (cuadro) picker — chooses the rail frame devices mount onto */}
          <div className="flex items-center gap-1.5">
            <span className="font-mono text-xs text-ink-4">{t('panel_board.enclosure')}</span>
            <div className="flex items-center gap-1">
              {ENCLOSURE_VARIANTS.map((v) => (
                <button
                  key={v.id}
                  type="button"
                  onClick={() => handleSelectEnclosure(v.id)}
                  className={
                    'rounded border px-2 py-1 font-mono text-xs transition-colors ' +
                    (v.id === enclosureId
                      ? 'border-accent bg-accent/10 text-accent'
                      : 'border-border text-ink-3 hover:bg-surface-3')
                  }
                >
                  {v.label}
                </button>
              ))}
            </div>
          </div>
          {/* Drop the backend preview gaps so the enclosure auto-fit is correct. */}
          <button
            type="button"
            onClick={handleCompact}
            title={t('panel_board.compact_hint')}
            className={
              'rounded border px-2 py-1 font-mono text-xs transition-colors ' +
              (compact
                ? 'border-accent bg-accent/10 text-accent'
                : 'border-border text-ink-3 hover:bg-surface-3')
            }
          >
            {t('panel_board.compact')}
          </button>
          <p className="text-xs text-ink-4">{t('panel_board.move_hint')}</p>
        </div>
      </div>

      {/* Enclosure capacity warning — chosen frame has fewer rails than the layout needs */}
      {overflowsEnclosure && (
        <div className="no-print rounded border border-warning/40 bg-warning/5 p-3 text-xs text-warning">
          ⚠ {t('panel_board.enclosure_overflow', { rows: requiredDeviceRows })}
        </div>
      )}

      {groupLimitBanner && (
        <div className="no-print rounded border border-warning/40 bg-warning/5 p-4 text-sm">
          <div className="font-sans text-sm font-semibold text-warning">
            {t('panel_board.group_limit_title')}
          </div>
          <div className="mt-2 space-y-3 text-xs leading-relaxed text-ink-3">
            <p>{t('panel_board.group_limit_body_1')}</p>
            <p>{t('panel_board.group_limit_body_2')}</p>
            <p>{t('panel_board.group_limit_body_3')}</p>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button type="button" size="sm" onClick={handleCreateGroupForLimitedCircuit}>
              {t('panel_board.create_new_group')}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setGroupLimitBanner(null)}
            >
              {t('panel_board.acknowledge')}
            </Button>
          </div>
        </div>
      )}

      {/* Left: canvas (visible in print). Right: groups/lines panel (prints too;
          its highlight is screen-only). On narrow screens the panel stacks below. */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_300px]">
        <PanelBoardCanvas
          layout={displayLayout}
          geometry={geometry}
          highlightedDeviceIds={highlight.deviceIds}
          deviceLineLabels={deviceLineLabels}
          selectedDeviceId={selectedDeviceId}
          placementActive={Boolean(selectedDeviceId)}
          onSelectDevice={handleSelectDevice}
          onOpenDeviceMenu={handleOpenDeviceMenu}
          onSlotClick={handleSlotClick}
          canPlaceSelectedAt={handleCanPlaceAt}
          canSwapSelectedAt={handleCanSwapAt}
          onMoveDevice={handleMoveDevice}
        />
        <GroupsLinesPanel
          groups={grouping.groups}
          circuits={grouping.circuits}
          highlight={highlight}
          onFocusGroup={handleFocusGroup}
          onFocusCircuit={handleFocusCircuit}
          getCircuitGroupMoveBlockReason={handleGetCircuitGroupMoveBlockReason}
          onMoveCircuitToGroup={handleMoveCircuitToGroup}
          getRcdGroupDeleteBlockReason={handleGetRcdGroupDeleteBlockReason}
          onDeleteRcdGroup={handleDeleteRcdGroup}
        />
      </div>

      {/* Device action menu (popover) anchored at the click point. Hosts breaker
          editing (rating, line name).
          Portaled to <body> so position:fixed is viewport-relative — the page's
          animate-fade-up transform would otherwise make fixed resolve against it. */}
      {selectedMcb && menuAnchor && createPortal(
        <div className="no-print">
          {/* Backdrop — a click anywhere closes the menu. */}
          <div className="fixed inset-0 z-40" onClick={() => handleSelectDevice(null)} />
          <div
            className="fixed z-50 w-56 rounded-lg border border-border bg-surface-2 p-3 text-xs shadow-lg"
            style={{
              left: Math.min(menuAnchor.x, window.innerWidth - 240),
              top: Math.min(menuAnchor.y, window.innerHeight - 200),
            }}
          >
            <div className="mb-2 flex items-center justify-between">
              <span className="font-mono text-ink-3">MCB · {selectedMcb.ratingA}&thinsp;A</span>
              <button
                type="button"
                onClick={() => handleSelectDevice(null)}
                className="text-ink-4 hover:text-ink"
                aria-label={t('panel_board.cancel')}
              >
                ✕
              </button>
            </div>
            <label className="block">
              <span className="font-mono text-ink-4">{t('panel_board.rating')}</span>
              <select
                value={selectedMcb.ratingA}
                onChange={(e) => handleChangeRating(Number(e.target.value))}
                className="mt-1 w-full rounded border border-border bg-surface px-2 py-1 font-mono text-xs text-ink-3 hover:bg-surface-3"
              >
                {MCB_RATINGS.map((r) => (
                  <option key={r} value={r}>{r} A</option>
                ))}
              </select>
            </label>
            {selectedCircuit && (
              <label className="mt-2 block">
                <span className="font-mono text-ink-4">{t('panel_board.line_name')}</span>
                <input
                  type="text"
                  value={selectedCircuit.label}
                  onChange={(e) => handleChangeLineName(e.target.value)}
                  placeholder="C5a"
                  className="mt-1 w-full rounded border border-border bg-surface px-2 py-1 font-mono text-xs text-ink-3 hover:bg-surface-3"
                />
              </label>
            )}
          </div>
        </div>,
        document.body,
      )}

      {/* Pre-print disclaimer. Portaled to <body> so it stays outside the
          data-print-area and never appears in the print output. */}
      {printDisclaimerOpen && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 px-4 py-6">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="panel-print-disclaimer-title"
            className="w-full max-w-md rounded-lg border border-border bg-surface p-5 text-ink shadow-xl"
          >
            <h2 id="panel-print-disclaimer-title" className="text-base font-semibold">
              {t('panel_board.disclaimer_title')}
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-ink-3">{t('panel_board.disclaimer_p1')}</p>
            <p className="mt-3 text-sm leading-relaxed text-ink-3">{t('panel_board.disclaimer_p2')}</p>
            <div className="mt-5 flex justify-end">
              <Button variant="primary" type="button" onClick={handleConfirmPrint}>
                {t('panel_board.disclaimer_confirm')}
              </Button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
